/**
 * Mobile App Banner — utility for smart app install prompts.
 *
 * Detects mobile browser users (not native app) and manages
 * dismiss state via localStorage so prompts don't reappear.
 */

const DISMISS_KEY = "tm_app_banner_dismissed";
const DISMISS_TTL_DAYS = 3; // Re-show after 3 days

export const APP_STORE_URL = "https://apps.apple.com/gb/app/transportmate/id6759405741";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=uk.co.transportmate.app";

/**
 * Custom URI scheme for deep-linking into the installed app.
 * MUST stay in sync with:
 *   - ios.scheme in capacitor.config.ts
 *   - android intent-filter scheme in AndroidManifest.xml
 *   - custom_url_scheme in android/app/src/main/res/values/strings.xml
 *   - Deep link parsing in src/App.tsx DeepLinkHandler
 */
export const APP_DEEP_LINK = "transportmate://";

/** Detect iOS browser (not native) */
export function isIOSBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS =
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  // Exclude Capacitor native
  return isIOS && !/Capacitor/i.test(ua);
}

/** Detect Android browser (not native) */
export function isAndroidBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("android") && !/capacitor/i.test(ua);
}

/** True if user is on a mobile browser (not the native app) */
export function isMobileBrowser(): boolean {
  return isIOSBrowser() || isAndroidBrowser();
}

/** Get the correct store URL for the current platform */
export function getStoreUrl(): string {
  return isIOSBrowser() ? APP_STORE_URL : PLAY_STORE_URL;
}

/** Get store name for display */
export function getStoreName(): string {
  return isIOSBrowser() ? "App Store" : "Google Play";
}

/** Check if user has dismissed the banner recently */
export function isBannerDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissed = JSON.parse(raw);
    const expiresAt = dismissed.at + DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() > expiresAt) {
      localStorage.removeItem(DISMISS_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Mark the banner as dismissed */
export function dismissBanner(): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ at: Date.now() }));
  } catch {}
}

/**
 * Try to open the native app via deep link.
 * If app is not installed the deep link silently fails;
 * after a short delay we redirect to the appropriate store.
 *
 * Optionally pass a path to deep-link to a specific screen,
 * e.g. tryDeepLinkWithFallback("/job/abc-123")
 */
export function tryDeepLinkWithFallback(path?: string): void {
  const deepLink = path ? `${APP_DEEP_LINK}${path.replace(/^\//, "")}` : APP_DEEP_LINK;
  const storeUrl = getStoreUrl();

  // Record the time so we can detect if the app opened (page will blur)
  const start = Date.now();
  window.location.href = deepLink;

  setTimeout(() => {
    // If the page is still visible after 1.5s the app probably isn't installed
    if (document.visibilityState !== "hidden" && Date.now() - start < 2000) {
      window.location.href = storeUrl;
    }
  }, 1500);
}
